import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import AddListingModal from './AddListingModal';
import BookingCalendar from './BookingCalendar';

const { FiBriefcase, FiPlus, FiCheck, FiX, FiTrash2, FiEye, FiEyeOff, FiCalendar, FiUser, FiRefreshCw, FiClock, FiDollarSign, FiMail, FiList } = FiIcons;

const LISTINGS_TABLE = 'merchant_listings_1777866100042';
const BOOKINGS_TABLE = 'service_bookings_1777866100042';

const STATUS_STYLES = {
  pending_admin: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_merchant: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
  confirmed: 'bg-green-50 text-green-700 border-green-200',
  completed: 'bg-purple-50 text-purple-700 border-purple-200',
  refunded: 'bg-orange-50 text-orange-700 border-orange-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  no_show: 'bg-stone-50 text-stone-700 border-stone-200'
};

const ListingsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('services');
  const [bookingsView, setBookingsView] = useState('list'); // 'list' | 'calendar'
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [autoApprove, setAutoApprove] = useState(() => localStorage.getItem('merchant_auto_approve_bookings') === 'true');
  
  const [user] = useState(() => getSession());

  const toggleAutoApprove = (val) => {
    setAutoApprove(val);
    localStorage.setItem('merchant_auto_approve_bookings', val);
  };

  const fetchListings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await writeFetch(`read/${LISTINGS_TABLE}?limit=500`);
      const data = await res.json();
      
      let allListings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allListings = data.data.filter(l => String(l.owner_id) === String(user.id));
      }

      const mockKey = `mock_listings_${user.id}`;
      const mockListings = JSON.parse(localStorage.getItem(mockKey) || '[]');
      allListings = [...allListings, ...mockListings.filter(m => !allListings.find(a => a.id === m.id))];
      
      setListings(allListings);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
    setLoading(false);
  }, [user]);

  const fetchBookings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Fetch ALL bookings first, then filter locally for cross-tenant safety
      const res = await writeFetch(`read/${BOOKINGS_TABLE}?limit=500`);
      const data = await res.json();
      
      let allBookings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allBookings = data.data.filter(b => String(b.merchant_id) === String(user.id));
      }

      // Sync mock bookings
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          try {
            const mockBookings = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(mockBookings)) {
              allBookings = [...allBookings, ...mockBookings.filter(b => String(b.merchant_id) === String(user.id) && !allBookings.find(a => a.id === b.id))];
            }
          } catch(e) {}
        }
      }
      
      setBookings(allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (activeSubTab === 'services') fetchListings();
    else if (activeSubTab === 'bookings') fetchBookings();
  }, [activeSubTab, fetchListings, fetchBookings]);

  const handleSetAvailability = async (listingId, newStatus) => {
    if (!user) return;
    try {
      await writeFetch(`update/${LISTINGS_TABLE}/${listingId}`, {
        method: 'PUT',
        body: JSON.stringify({ availability_status: newStatus })
      });
      fetchListings();
    } catch (error) {
      console.error('Error updating listing:', error);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!user) return;
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await writeFetch(`delete/${LISTINGS_TABLE}/${listingId}`, {
        method: 'DELETE'
      });
      fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await writeFetch(`update/${BOOKINGS_TABLE}/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          try {
            const mockBookings = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(mockBookings)) {
              const updated = mockBookings.map(b => b.id === bookingId ? { ...b, status: newStatus } : b);
              localStorage.setItem(key, JSON.stringify(updated));
            }
          } catch(e) {}
        }
      }
      
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const getListingTitle = (listingId) => {
    const listing = listings.find(l => l.id === listingId);
    return listing?.title || 'Unknown Service';
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending_merchant' || b.status === 'approved' || b.status === 'pending_admin' || b.status === 'pending');
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled' || b.status === 'rejected');

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-1">
        <button onClick={() => setActiveSubTab('services')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${
          activeSubTab === 'services' ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
        }`}>
          <SafeIcon icon={FiBriefcase} className="w-3.5 h-3.5" /> My Services
        </button>
        <button onClick={() => setActiveSubTab('bookings')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all relative ${
          activeSubTab === 'bookings' ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
        }`}>
          <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" /> Bookings
          {pendingBookings.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center">{pendingBookings.length}</span>
          )}
        </button>
      </div>

      {activeSubTab === 'services' && (
        <div className="space-y-6">
          <div className="flex justify-end mb-6">
            <button 
              onClick={() => setShowAddModal(true)} 
              className="w-full md:w-auto px-6 py-4 bg-accent-gold text-accent-navy rounded-[24px] text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all shadow-md flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add New Service
            </button>
          </div>

          <div className="bg-white rounded-[32px] border border-brand-dark p-8">
            <div className="flex items-center justify-between pb-5 border-b border-brand-dark mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-2xl bg-brand-light">
                  <SafeIcon icon={FiBriefcase} className="w-5 h-5 text-accent-navy" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-accent-navy">My Services</h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Manage your service listings</p>
                </div>
              </div>
              <div className="bg-accent-gold/10 border border-accent-gold/30 px-4 py-2 rounded-xl flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-accent-gold w-3.5 h-3.5" />
                <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{listings.length} Active</span>
              </div>
            </div>

            {listings.length === 0 ? (
              <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
                <SafeIcon icon={FiBriefcase} className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h4 className="text-lg font-serif text-accent-navy mb-1">No Services Yet</h4>
                <p className="text-text-muted text-sm font-light">Create your first service offering to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {listings.map(listing => (
                  <div key={listing.id} className="bg-white p-5 rounded-[24px] border border-brand-dark shadow-sm hover:border-accent-gold transition-all">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h4 className="font-bold text-accent-navy truncate">{listing.title}</h4>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                            listing.availability_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {listing.availability_status === 'active' ? 'Active' : 'Hidden'}
                          </span>
                          {listing.pricing_model === 'free' ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[9px] font-black uppercase">FREE</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase border border-blue-100">PAID</span>
                          )}
                        </div>
                        <p className="text-sm text-text-muted line-clamp-2">{listing.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-text-muted mt-3">
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                            <span>{listing.duration || '60'} min</span>
                          </div>
                          {listing.price > 0 && (
                            <div className="flex items-center gap-1.5">
                              <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5 text-green-500" />
                              <span className="font-bold text-green-600">${parseFloat(listing.price).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                            <span className="capitalize">{listing.appointment_type || 'physical'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 w-full lg:w-auto shrink-0 border-t lg:border-t-0 border-brand-dark pt-4 lg:pt-0">
                        <button onClick={() => handleSetAvailability(listing.id, 'active')}
                          disabled={listing.availability_status === 'active'}
                          className={`flex-1 lg:flex-none px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest border ${
                            listing.availability_status === 'active' ? 'bg-green-50 text-green-600 border-green-200 opacity-50 cursor-not-allowed' : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                          }`}>
                          <SafeIcon icon={FiEye} className="w-4 h-4" /> Show
                        </button>
                        <button onClick={() => handleSetAvailability(listing.id, 'inactive')}
                          disabled={listing.availability_status === 'inactive'}
                          className={`flex-1 lg:flex-none px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest border ${
                            listing.availability_status === 'inactive' ? 'bg-amber-50 text-amber-600 border-amber-200 opacity-50 cursor-not-allowed' : 'bg-white text-amber-600 border-amber-200 hover:bg-amber-50'
                          }`}>
                          <SafeIcon icon={FiEyeOff} className="w-4 h-4" /> Hide
                        </button>
                        <button onClick={() => handleDeleteListing(listing.id)}
                          className="flex-1 lg:flex-none px-4 py-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-gray-200">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <AddListingModal isOpen={showAddModal} onClose={() => { setShowAddModal(false); fetchListings(); }} />
        </div>
      )}

      {activeSubTab === 'bookings' && (
        <div className="space-y-6">
          {/* View Toggle + Auto-approve */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex bg-white border border-brand-dark rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => setBookingsView('list')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  bookingsView === 'list' ? 'bg-accent-navy text-white shadow-md' : 'text-text-muted hover:text-accent-navy'
                }`}
              >
                <SafeIcon icon={FiList} className="w-3.5 h-3.5" /> List
              </button>
              <button
                onClick={() => setBookingsView('calendar')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  bookingsView === 'calendar' ? 'bg-accent-navy text-white shadow-md' : 'text-text-muted hover:text-accent-navy'
                }`}
              >
                <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-brand-dark flex items-center justify-between w-full sm:w-auto gap-4">
              <div>
                <p className="text-[12px] font-bold text-accent-navy">Auto-Approve</p>
                <p className="text-[10px] text-text-muted uppercase tracking-widest">Auto-confirm incoming bookings</p>
              </div>
              <button onClick={() => toggleAutoApprove(!autoApprove)} type="button"
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 shadow-inner ${autoApprove ? 'bg-green-500' : 'bg-gray-200'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoApprove ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          {bookingsView === 'calendar' ? (
            <BookingCalendar
              bookings={bookings}
              onUpdateStatus={updateBookingStatus}
              loading={loading}
              getListingTitle={getListingTitle}
            />
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center">
                  <p className="text-2xl font-serif text-amber-600">{pendingBookings.length}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Pending</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center">
                  <p className="text-2xl font-serif text-green-600">{confirmedBookings.length}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Confirmed</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center">
                  <p className="text-2xl font-serif text-purple-600">{completedBookings.length}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Completed</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center">
                  <p className="text-2xl font-serif text-gray-500">{cancelledBookings.length}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Cancelled</p>
                </div>
              </div>

              {/* List View */}
              <div className="bg-white rounded-[32px] border border-brand-dark p-8">
                <div className="flex items-center justify-between pb-5 border-b border-brand-dark mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-brand-light">
                      <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent-navy" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-accent-navy">Customer Bookings</h3>
                      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Manage appointments and reservations</p>
                    </div>
                  </div>
                  <button onClick={fetchBookings} className="p-3 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:border-accent-gold transition-all">
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  </button>
                </div>

                {bookings.length === 0 ? (
                  <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
                    <SafeIcon icon={FiCalendar} className="w-12 h-12 text-text-muted mx-auto mb-4" />
                    <h4 className="text-lg font-serif text-accent-navy mb-1">No Bookings Yet</h4>
                    <p className="text-text-muted text-sm font-light">Customer bookings will appear here once they schedule appointments.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingBookings.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-accent-navy mb-3 flex items-center gap-2">
                          <SafeIcon icon={FiClock} className="w-4 h-4 text-amber-500" />
                          Pending Requests ({pendingBookings.length})
                        </h4>
                        {pendingBookings.map(booking => {
                          const isPendingAdmin = booking.status === 'pending' || booking.status === 'pending_admin';
                          return (
                          <div key={booking.id} className="bg-amber-50 p-5 rounded-[24px] border border-amber-200 mb-4">
                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${STATUS_STYLES['pending_merchant']}`}>
                                    {isPendingAdmin ? 'Pending Admin' : 'Pending Merchant Confirmation'}
                                  </span>
                                  <p className="font-bold text-accent-navy truncate">{booking.listing_title || getListingTitle(booking.listing_id)}</p>
                                  {booking.pricing_model === 'free' ? (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[9px] font-black uppercase">FREE</span>
                                  ) : (
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase border border-blue-100">PAID</span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                                  <div className="flex items-center gap-1.5">
                                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                                    <span className="font-medium text-accent-navy">{booking.customer_name}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <SafeIcon icon={FiMail} className="w-3.5 h-3.5" />
                                    <span>{booking.customer_email}</span>
                                  </div>
                                  {booking.price > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5 text-green-500" />
                                      <span className="font-bold text-green-600">${parseFloat(booking.price).toFixed(2)}</span>
                                    </div>
                                  )}
                                </div>
                                {booking.start_time && (
                                  <div className="mt-2 text-xs text-text-muted flex items-center gap-1.5">
                                    <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                                    <span>Requested For: {new Date(booking.start_time).toLocaleString()}</span>
                                  </div>
                                )}
                                {booking.notes && (
                                  <div className="mt-2 text-xs text-text-muted">
                                    <span className="font-medium">Notes:</span> {booking.notes}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2 w-full lg:w-auto shrink-0 border-t lg:border-t-0 border-amber-200 pt-4 lg:pt-0">
                                {isPendingAdmin ? (
                                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 italic flex items-center justify-center px-4 py-2.5">
                                    Awaiting Admin Approval
                                  </span>
                                ) : (
                                  <>
                                    <button onClick={() => updateBookingStatus(booking.id, 'rejected')}
                                      className="flex-1 lg:flex-none px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-red-200">
                                      <SafeIcon icon={FiX} className="w-4 h-4" /> Reject
                                    </button>
                                    <button onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                      className="flex-1 lg:flex-none px-4 py-2.5 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all flex items-center justify-center gap-2 shadow-md">
                                      <SafeIcon icon={FiCheck} className="w-4 h-4" /> Approve
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )})}
                      </div>
                    )}

                    {confirmedBookings.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-accent-navy mb-3 flex items-center gap-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                          Confirmed ({confirmedBookings.length})
                        </h4>
                        {confirmedBookings.map(booking => (
                          <div key={booking.id} className="bg-green-50 p-5 rounded-[24px] border border-green-200 mb-4">
                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${STATUS_STYLES['confirmed']}`}>Confirmed</span>
                                  <p className="font-bold text-accent-navy truncate">{booking.listing_title || getListingTitle(booking.listing_id)}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                                  <div className="flex items-center gap-1.5">
                                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                                    <span className="font-medium text-accent-navy">{booking.customer_name}</span>
                                  </div>
                                  {booking.start_time && (
                                    <div className="flex items-center gap-1.5">
                                      <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                                      <span>{new Date(booking.start_time).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 w-full lg:w-auto shrink-0 border-t lg:border-t-0 border-green-200 pt-4 lg:pt-0">
                                <button onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  className="flex-1 lg:flex-none px-4 py-2.5 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-purple-200">
                                  <SafeIcon icon={FiCheck} className="w-4 h-4" /> Complete
                                </button>
                                <button onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  className="flex-1 lg:flex-none px-4 py-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-gray-200">
                                  <SafeIcon icon={FiX} className="w-4 h-4" /> Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {completedBookings.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-accent-navy mb-3 flex items-center gap-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-500" />
                          Completed ({completedBookings.length})
                        </h4>
                        {completedBookings.map(booking => (
                          <div key={booking.id} className="bg-purple-50 p-5 rounded-[24px] border border-purple-200 mb-4 opacity-75">
                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${STATUS_STYLES['completed']}`}>Completed</span>
                                  <p className="font-bold text-accent-navy truncate">{booking.listing_title || getListingTitle(booking.listing_id)}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                                  <div className="flex items-center gap-1.5">
                                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                                    <span>{booking.customer_name}</span>
                                  </div>
                                  {booking.start_time && (
                                    <div className="flex items-center gap-1.5">
                                      <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                                      <span>{new Date(booking.start_time).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {cancelledBookings.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-accent-navy mb-3 flex items-center gap-2">
                          <SafeIcon icon={FiX} className="w-4 h-4 text-gray-500" />
                          Cancelled/Rejected ({cancelledBookings.length})
                        </h4>
                        {cancelledBookings.map(booking => (
                          <div key={booking.id} className="bg-gray-50 p-5 rounded-[24px] border border-gray-200 mb-4 opacity-60">
                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${STATUS_STYLES[booking.status] || STATUS_STYLES['cancelled']}`}>
                                    {booking.status}
                                  </span>
                                  <p className="font-bold text-accent-navy truncate">{booking.listing_title || getListingTitle(booking.listing_id)}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                                  <div className="flex items-center gap-1.5">
                                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                                    <span>{booking.customer_name}</span>
                                  </div>
                                  {booking.start_time && (
                                    <div className="flex items-center gap-1.5">
                                      <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                                      <span>{new Date(booking.start_time).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingsTab;